import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import useAppVersionsEvents from '../hooks/useAppVersionsEvents';
import { getApiUrl } from '../utils/api';

const AppVersions = ({ onNavigateToDashboard }) => {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filteredApps, setFilteredApps] = useState([]);
    const [filters, setFilters] = useState({
        typology: '',
        cluster: '',
        app: '',
        appType: 'octo' // Default to Octo apps
    });
    const [uniqueTypologies, setUniqueTypologies] = useState([]);
    const [uniqueClusters, setUniqueClusters] = useState([]);
    const [uniqueApps, setUniqueApps] = useState([]);

    useEffect(() => {
        fetchAppVersions();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [apps, filters]);

    // Real-time updates handler
    const handleAppVersionsUpdate = useCallback((updatedApps) => {
        setApps(updatedApps);
        
        // Extract unique values for filters
        const typologies = [...new Set(updatedApps.map(app => app.typology).filter(Boolean))];
        const clusters = [...new Set(updatedApps.map(app => app.cluster).filter(Boolean))];
        const apps = [...new Set(updatedApps.map(app => app.app).filter(Boolean))];
        
        setUniqueTypologies(typologies);
        setUniqueClusters(clusters);
        setUniqueApps(apps);
        
        toast.success('App versions updated automatically');
    }, []);

    // Connect to real-time updates
    useAppVersionsEvents(handleAppVersionsUpdate);

    const fetchAppVersions = async () => {
        try {
            setLoading(true);
            const response = await axios.get(getApiUrl('/api/app-versions'));
            setApps(response.data || []);
            
            // Extract unique values for filters
            const typologies = [...new Set(response.data.map(app => app.typology).filter(Boolean))];
            const clusters = [...new Set(response.data.map(app => app.cluster).filter(Boolean))];
            const apps = [...new Set(response.data.map(app => app.app).filter(Boolean))];
            
            setUniqueTypologies(typologies);
            setUniqueClusters(clusters);
            setUniqueApps(apps);
        } catch (error) {
            console.error('Error fetching app versions:', error);
            toast.error('Failed to load app versions');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...apps];

        // Filter by app type (Octo apps vs All apps)
        if (filters.appType === 'octo') {
            filtered = filtered.filter(app => app.appType === 'octo');
        }
        // If appType is 'all', show all apps (no filtering needed)

        if (filters.typology) {
            filtered = filtered.filter(app => app.typology === filters.typology);
        }
        if (filters.cluster) {
            filtered = filtered.filter(app => app.cluster === filters.cluster);
        }
        if (filters.app) {
            filtered = filtered.filter(app => app.app === filters.app);
        }

        setFilteredApps(filtered);
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            typology: '',
            cluster: '',
            app: '',
            appType: 'octo' // Reset to Octo apps default
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'success': return 'success';
            case 'failed': return 'danger';
            case 'running': return 'primary';
            case 'stopped': return 'warning';
            default: return 'secondary';
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="app-versions-page">
            <motion.div 
                className="container-fluid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="mb-0">
                                    <i className="bi bi-list-check me-2"></i>
                                    App Versions
                                </h2>
                                <p className="text-muted mb-0">View all application versions across typologies and clusters</p>
                            </div>
                            <button 
                                className="btn btn-outline-secondary"
                                onClick={onNavigateToDashboard}
                            >
                                <i className="bi bi-arrow-left me-2"></i>
                                Back to Dashboard
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <motion.div 
                    className="row mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <div className="col-12">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-light border-0">
                                <h5 className="mb-0">
                                    <i className="bi bi-funnel me-2"></i>
                                    Filters
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">App Type Filter</label>
                                        <div className="btn-group w-100" role="group">
                                            <input 
                                                type="radio" 
                                                className="btn-check" 
                                                name="appTypeFilter" 
                                                id="octoApps" 
                                                value="octo"
                                                checked={filters.appType === 'octo'}
                                                onChange={(e) => handleFilterChange('appType', e.target.value)}
                                            />
                                            <label className="btn btn-outline-primary" htmlFor="octoApps">
                                                <i className="bi bi-app me-1"></i>
                                                Octo Apps
                                            </label>
                                            
                                            <input 
                                                type="radio" 
                                                className="btn-check" 
                                                name="appTypeFilter" 
                                                id="allApps" 
                                                value="all"
                                                checked={filters.appType === 'all'}
                                                onChange={(e) => handleFilterChange('appType', e.target.value)}
                                            />
                                            <label className="btn btn-outline-primary" htmlFor="allApps">
                                                <i className="bi bi-collection me-1"></i>
                                                All Apps
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-3 mb-3">
                                        <label className="form-label fw-bold">Typology</label>
                                        <select 
                                            className="form-select"
                                            value={filters.typology}
                                            onChange={(e) => handleFilterChange('typology', e.target.value)}
                                        >
                                            <option value="">All Typologies</option>
                                            {uniqueTypologies.map(typology => (
                                                <option key={typology} value={typology}>
                                                    {typology}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <label className="form-label fw-bold">Cluster</label>
                                        <select 
                                            className="form-select"
                                            value={filters.cluster}
                                            onChange={(e) => handleFilterChange('cluster', e.target.value)}
                                        >
                                            <option value="">All Clusters</option>
                                            {uniqueClusters.map(cluster => (
                                                <option key={cluster} value={cluster}>
                                                    {cluster}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <label className="form-label fw-bold">App</label>
                                        <select 
                                            className="form-select"
                                            value={filters.app}
                                            onChange={(e) => handleFilterChange('app', e.target.value)}
                                        >
                                            <option value="">All Apps</option>
                                            {uniqueApps.map(app => (
                                                <option key={app} value={app}>
                                                    {app}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3 mb-3 d-flex align-items-end">
                                        <button 
                                            className="btn btn-outline-secondary w-100"
                                            onClick={clearFilters}
                                        >
                                            <i className="bi bi-x-circle me-2"></i>
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <small className="text-muted">
                                            Showing {filteredApps.length} of {apps.length} applications
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Apps Table */}
                <motion.div 
                    className="row"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    <div className="col-12">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-light border-0 d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="bi bi-table me-2"></i>
                                    Application Versions
                                </h5>
                                <button 
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={fetchAppVersions}
                                >
                                    <i className="bi bi-arrow-clockwise me-1"></i>
                                    Refresh
                                </button>
                            </div>
                            <div className="card-body">
                                {filteredApps.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>App</th>
                                                    <th>Type</th>
                                                    <th>Typology</th>
                                                    <th>Cluster</th>
                                                    <th>Octo Version</th>
                                                    <th>Infra Version</th>
                                                    <th>Last Update</th>
                                                    <th>Updated By</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredApps.map((app, index) => (
                                                    <motion.tr 
                                                        key={app.id || index}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: 0.1 * index }}
                                                    >
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <i className="bi bi-app me-2 text-primary"></i>
                                                                <strong>{app.app}</strong>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${app.appType === 'octo' ? 'bg-primary' : 'bg-secondary'}`}>
                                                                {app.appType === 'octo' ? 'Octo+' : 'Infra'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-info">{app.typology}</span>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-secondary">{app.cluster}</span>
                                                        </td>
                                                        <td>
                                                            {app.appType === 'octo' ? (
                                                                app.octoVersion ? (
                                                                    <span className="badge bg-success">{app.octoVersion}</span>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {app.infraVersion ? (
                                                                <span className="badge bg-warning text-dark">{app.infraVersion}</span>
                                                            ) : (
                                                                <span className="text-muted">N/A</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <small className="text-muted">
                                                                {formatDate(app.lastUpdated)}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            {app.updatedBy ? (
                                                                <div className="d-flex align-items-center">
                                                                    <i className="bi bi-person-circle me-1"></i>
                                                                    <small>{app.updatedBy}</small>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">N/A</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {app.status ? (
                                                                <span className={`badge bg-${getStatusColor(app.status)}`}>
                                                                    {app.status}
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">N/A</span>
                                                            )}
                                                        </td>
                                                    </motion.tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <i className="bi bi-inbox display-4 text-muted"></i>
                                        <h5 className="text-muted mt-3">No applications found</h5>
                                        <p className="text-muted">Try adjusting your filters or refresh the data</p>
                                        <button 
                                            className="btn btn-outline-primary"
                                            onClick={clearFilters}
                                        >
                                            <i className="bi bi-funnel me-2"></i>
                                            Clear Filters
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default AppVersions; 